import React from 'react';
import { attendeeListService } from '../utils/attendeeListService';

/**
 * PrintableAttendeeList Component
 * Hidden on screen, only visible when printing
 * Professional layout for meeting attendee list
 */
const PrintableAttendeeList = ({ attendeeData }) => {
  if (!attendeeData) return null;

  const { meeting, physicians, salesRep, staffMembers } = attendeeData;

  // For backward compatibility, also support old 'customer' field
  const allPhysicians = physicians || (attendeeData.customer ? [attendeeData.customer] : []);

  // Format date and time
  const formattedDate = attendeeListService.formatDate(meeting.date);
  const formattedStartTime = attendeeListService.formatTime(meeting.startTime);
  const formattedEndTime = attendeeListService.formatTime(meeting.endTime);
  const timeRange = formattedEndTime ? `${formattedStartTime} - ${formattedEndTime}` : formattedStartTime;

  return (
    <div id="printable-attendee-list" className="print-only">
      {/* Header Section */}
      <div className="print-header">
        <div className="print-logo-section">
          <img
            src="/logo512.png"
            alt="RepConnect"
            className="print-logo-image"
          />
          <div className="print-brand-name">RepConnect</div>
          <div className="print-website">www.meetwithreps.com</div>
        </div>

        <div className="print-title-section">
          <h1>MEETING ATTENDEE LIST</h1>
        </div>
      </div>

      {/* Meeting Information Section */}
      <div className="print-meeting-info">
        <div className="print-info-grid">
          <div className="print-info-item">
            <span className="print-label">Meeting Date:</span>
            <span className="print-value">{formattedDate}</span>
          </div>
          <div className="print-info-item">
            <span className="print-label">Meeting Time:</span>
            <span className="print-value">{timeRange}</span>
          </div>
          <div className="print-info-item">
            <span className="print-label">Restaurant:</span>
            <span className="print-value">{meeting.restaurant || 'TBD'}</span>
          </div>
        </div>
      </div>

      {/* Compact Contact Information */}
      <div className="print-section">
        <h2 className="print-section-title">Meeting Participants</h2>
        <div className="print-contacts-grid">
          {/* Healthcare Providers */}
          {allPhysicians.map((physician, index) => (
            <div key={physician.id || index} className="print-contact-compact">
              <div className="print-contact-label">
                Healthcare Provider{allPhysicians.length > 1 ? ` ${index + 1}` : ''}
                {physician.isOrganizer && ' (Organizer)'}:
              </div>
              <div className="print-contact-name-inline">{physician.name}, {physician.title}</div>
              {physician.company && <div className="print-contact-detail-inline">{physician.company}</div>}
              <div className="print-contact-detail-inline">
                {physician.email && <span>{physician.email}</span>}
                {physician.email && physician.phone && <span> • </span>}
                {physician.phone && <span>{physician.phone}</span>}
              </div>
              {physician.npi && (
                <div className="print-contact-detail-inline">
                  <strong>NPI:</strong> {physician.npi}
                </div>
              )}
            </div>
          ))}

          {/* Sales Representative */}
          {salesRep && (
            <div className="print-contact-compact">
              <div className="print-contact-label">Sales Representative:</div>
              <div className="print-contact-name-inline">{salesRep.name}, {salesRep.title}</div>
              {salesRep.company && <div className="print-contact-detail-inline">{salesRep.company}</div>}
              <div className="print-contact-detail-inline">
                {salesRep.email && <span>{salesRep.email}</span>}
                {salesRep.email && salesRep.phone && <span> • </span>}
                {salesRep.phone && <span>{salesRep.phone}</span>}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Attendee List Section */}
      <div className="print-section">
        <h2 className="print-section-title">Meeting Attendees</h2>
        <div className="print-separator-line"></div>

        {/* All Physicians */}
        {allPhysicians.map((physician, index) => (
          <div key={physician.id || index} className="print-attendee-row">
            <div className="print-attendee-number">{index + 1}.</div>
            <div className="print-attendee-info">
              <span className="print-attendee-name">
                {physician.name}
                {physician.isOrganizer && ' (Organizer)'}
              </span>
              <span className="print-attendee-role">{physician.title}</span>
            </div>
            <div className="print-attendee-signature">
              <div className="signature-line"></div>
              <span className="signature-label">Signature</span>
            </div>
          </div>
        ))}

        {/* Sales Rep */}
        {salesRep && (
          <div className="print-attendee-row">
            <div className="print-attendee-number">{allPhysicians.length + 1}.</div>
            <div className="print-attendee-info">
              <span className="print-attendee-name">{salesRep.name}</span>
              <span className="print-attendee-role">{salesRep.title}</span>
            </div>
            <div className="print-attendee-signature">
              <div className="signature-line"></div>
              <span className="signature-label">Signature</span>
            </div>
          </div>
        )}

        {/* Staff Members */}
        {staffMembers && staffMembers.length > 0 && staffMembers.map((staff, index) => (
          <div key={staff.id} className="print-attendee-row">
            <div className="print-attendee-number">{allPhysicians.length + (salesRep ? 1 : 0) + index + 1}.</div>
            <div className="print-attendee-info">
              <span className="print-attendee-name">{staff.name}</span>
              <span className="print-attendee-role">{staff.role}</span>
            </div>
            <div className="print-attendee-signature">
              <div className="signature-line"></div>
              <span className="signature-label">Signature</span>
            </div>
          </div>
        ))}
      </div>

      {/* Footer with total count and timestamp */}
      <div className="print-footer">
        <div className="print-separator-line"></div>
        <div className="print-footer-content">
          <div>
            <strong>Total Attendees:</strong> {allPhysicians.length + (salesRep ? 1 : 0) + (staffMembers?.length || 0)}
          </div>
          <div className="print-generated-date">
            Generated: {new Date().toLocaleDateString('en-US', {
              year: 'numeric',
              month: 'long',
              day: 'numeric',
              hour: 'numeric',
              minute: '2-digit',
              hour12: true
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrintableAttendeeList;
